import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ICreateComment,
  ICreateTicket,
  IPagedResult,
  IPatchTicket,
  ITicketComment,
  ITicketDetail,
  ITicketListItem,
  ITicketQuery,
  ITicketSummary,
} from '../models/ITicket.interface';

/**
 * Talks to BC_Ticketing. The bearer token and `X-BC-Tenant` are attached globally by
 * `MsalCustomerInterceptor` (the ticketing endpoint is registered in its module-API list), so nothing
 * here sets headers.
 *
 * Unlike the other module services this one does not put the company in the route: ticketing keeps one
 * database for the whole platform and scopes by the (tenant, company) pair, so the company travels as
 * a parameter and the API decides what the caller may see. See
 * `docs/ticketing/CODEBASE-RESOLUTIONS.md` §2.
 */
@Injectable({ providedIn: 'root' })
export class TicketingApiService {
  private readonly http = inject(HttpClient);

  private readonly base = `${environment.apiConfig.ticketingAPI.endpoint}/api/tickets`;

  /**
   * Multipart, because the screenshot travels with the ticket: one request, so a ticket and its
   * screenshot either both arrive or neither does. The JSON goes in a `ticket` form field — a request
   * cannot carry both a JSON body and file parts.
   *
   * No `Content-Type` is set on purpose: the browser has to add the multipart boundary itself, and
   * setting it by hand is the classic way to get a 400 that looks like a server bug.
   */
  create(ticket: ICreateTicket, screenshot: Blob | null, files: File[] = []): Observable<ITicketDetail> {
    const form = new FormData();
    form.append('ticket', JSON.stringify(ticket));

    if (screenshot) {
      form.append('screenshot', screenshot, 'screenshot.png');
    }

    for (const file of files) {
      form.append('files', file, file.name);
    }

    return this.http.post<ITicketDetail>(this.base, form);
  }

  query(query: ITicketQuery): Observable<IPagedResult<ITicketListItem>> {
    let params = new HttpParams()
      .set('scope', query.scope)
      .set('companyId', query.companyId);

    // Each appended only when set. An empty `status=` would bind as 0 — which is a real status (New),
    // so sending it would silently filter the list instead of leaving it unfiltered.
    if (query.status != null) params = params.set('status', query.status);
    if (query.type != null) params = params.set('type', query.type);
    if (query.openOnly) params = params.set('openOnly', true);
    if (query.module) params = params.set('module', query.module);
    if (query.mineOrUnassigned) params = params.set('mineOrUnassigned', true);
    if (query.page != null) params = params.set('page', query.page);
    if (query.pageSize != null) params = params.set('pageSize', query.pageSize);

    return this.http.get<IPagedResult<ITicketListItem>>(this.base, { params });
  }

  get(key: string): Observable<ITicketDetail> {
    return this.http.get<ITicketDetail>(`${this.base}/${encodeURIComponent(key)}`);
  }

  /** Counts for the footer badge. Called on navigation, so it stays cheap. */
  summary(companyId: number): Observable<ITicketSummary> {
    return this.http.get<ITicketSummary>(`${this.base}/summary`, {
      params: new HttpParams().set('companyId', companyId),
    });
  }

  addComment(key: string, comment: ICreateComment): Observable<ITicketComment> {
    return this.http.post<ITicketComment>(`${this.base}/${encodeURIComponent(key)}/comments`, comment);
  }

  close(key: string): Observable<ITicketDetail> {
    return this.http.post<ITicketDetail>(`${this.base}/${encodeURIComponent(key)}/close`, {});
  }

  reopen(key: string): Observable<ITicketDetail> {
    return this.http.post<ITicketDetail>(`${this.base}/${encodeURIComponent(key)}/reopen`, {});
  }

  /** Developer-only. Answers 409 when the row version is stale — the board relies on that. */
  patch(key: string, patch: IPatchTicket): Observable<ITicketDetail> {
    return this.http.patch<ITicketDetail>(`${this.base}/${encodeURIComponent(key)}`, patch);
  }
}
